import { InfoCard } from '@/components/features/agreements/listing/sub-components/InfoCard'
import { TitleSpan } from '@/components/features/shared/TitleSpan'
import { DebtLabel, InlinePriceLabel, PriceLabel } from '@/components/features/shared/utils'
import { AlertWarning } from '@/components/ui/alertWarning'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { installmentsOptions } from '@/constants/installmentOptions'
import { getCollectorFullName } from '@/domain/collectors/collector'
import { PaymentStatus } from '@/domain/payments/payments'
import { InstallmentPlanInfo } from '@/domain/subscriptions/installments'
import { useSellers } from '@/hooks/api/useSellers'
import { Calculator, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

interface ModifyPaymentPlanModalProps {
  isOpen: boolean
  onClose: () => void
  paymentPlan: InstallmentPlanInfo | null
  isPending: boolean
  onModify: (installments: number, installmentAmount: number) => void
  onSuccess?: () => void
}

export const ModifyPaymentPlanModal = ({
  isOpen,
  onClose,
  paymentPlan,
  isPending,
  onModify,
  onSuccess,
}: ModifyPaymentPlanModalProps) => {
  const { data: sellers = [] } = useSellers()
  const [installments, setInstallments] = useState<number>(3)
  const [installmentAmount, setInstallmentAmount] = useState<number>(0)

  const installmentValue = useMemo(() => {
    return installmentAmount / installments
  }, [installmentAmount, installments])

  const totalAmount = useMemo(() => {
    if (!paymentPlan) return 0
    return paymentPlan.initialAmount + paymentPlan.installmentAmount
  }, [paymentPlan])

  const totalPaid = useMemo(() => {
    return paymentPlan?.periods?.flatMap(p => p.payments)
      .filter(p => p.status === PaymentStatus.COMPLETED)
      .reduce((sum, p) => sum + p.amount, 0) || 0
  }, [paymentPlan])

  const totalOutstanding = useMemo(() => {
    return totalAmount - totalPaid
  }, [totalAmount, totalPaid])

  useEffect(() => {
    if (paymentPlan) {
      setInstallments(paymentPlan.installments)
      setInstallmentAmount(totalOutstanding)
    }
  }, [paymentPlan])

  const handleConfirm = () => {
    if (installments <= 0 || installmentAmount <= 0) return
    onModify(installments, installmentAmount)
    onSuccess?.()
  }

  if (!paymentPlan) {
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex flex-col items-start justify-between">
            <CardTitle className='text-xl'>Modificar Plan de pago </CardTitle>
            <CardDescription>
              <h3 className="text-md font-bold flex items-center gap-2">
                {paymentPlan.offeringCode} - {paymentPlan.offeringName}
              </h3>
            </CardDescription>
          </div>
        </DialogHeader>

        <CardContent className="space-y-6">
          <InfoCard
            rows={[
              [
                <div key="initial" className="flex flex-col gap-1">
                  <TitleSpan>Entrega Inicial</TitleSpan>
                  <PriceLabel price={paymentPlan.initialAmount} />
                </div>,
                <div key="total" className="flex flex-col gap-1">
                  <TitleSpan>Monto Total</TitleSpan>
                  <PriceLabel price={totalAmount} />
                </div>,
                <div key="paid" className="flex flex-col gap-1">
                  <TitleSpan>Cuotas Pagadas</TitleSpan>
                  <p className="text-sm font-bold">{paymentPlan.installmentsPaid} / {paymentPlan.installments}</p>
                </div>,
              ],
              [
                <div key="amount" className="flex flex-col gap-1">
                  <TitleSpan>Monto Abonado</TitleSpan>
                  <PriceLabel price={totalPaid} />
                </div>,
                <div key="outstanding" className="flex flex-col gap-1">
                  <TitleSpan>Saldo Pendiente</TitleSpan>
                  {totalOutstanding > 0 ? <DebtLabel amount={totalOutstanding} /> : <PriceLabel price={0} />}
                </div>,
                <div key="seller" className="flex flex-col justify-center gap-1">
                  <TitleSpan>Vendedor</TitleSpan>
                  <span className="text-xs">{getCollectorFullName(sellers.find(s => s.id === paymentPlan.sellerId))}</span>
                </div>,
              ],
            ]}
          />

          <Card className="my-4">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/20 shrink-0">
                <div className="space-y-2">
                  <Label>Monto a refinanciar</Label>
                  <Input
                    type="number"
                    value={installmentAmount || ''}
                    onChange={e => setInstallmentAmount(Number(e.target.value))}
                    disabled={isPending}
                    min={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cantidad de cuotas</Label>
                  <Select
                    value={installments.toString()}
                    onValueChange={v => setInstallments(Number(v))}
                    disabled={isPending}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar cuotas" />
                    </SelectTrigger>
                    <SelectContent>
                      {installmentsOptions.map(opt => (
                        <SelectItem key={opt} value={opt.toString()}>
                          {opt} cuotas
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 pt-2 text-sm text-muted-foreground flex justify-between border-t mt-2">
                  <span>Monto final por cuota:</span>
                  <span className="font-bold text-foreground">
                    ${installmentValue.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <AlertWarning>
            <p>
              El valor de cuota se calcula como <strong> <InlinePriceLabel price={installmentAmount} /> / {installments} = <InlinePriceLabel price={installmentValue} />.
              </strong>{totalOutstanding > 0 && <> <br /> Todos los cargos pendientes se eliminarán tras la confirmación.</>}
            </p>
          </AlertWarning>
        </CardContent>

        <DialogFooter className="mt-2">
          <div className='flex justify-between w-full'>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={handleConfirm}
              disabled={installments <= 0 || installmentAmount <= 0 || isPending}
            >
              <Calculator className="h-3.5 w-3.5 mr-1" />
              Modificar plan de pago
            </Button>
          </div>
        </DialogFooter>
      </DialogContent >
    </Dialog >
  )
}
